import React, { useEffect, useState } from 'react';
import { Filter, TrendingUp, BarChart3, Target, Zap } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { screenerService } from '../services/screenerService';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { WatchlistService } from '../services/api';

// Toggle between mock and live screener via env flag
// VITE_USE_MOCK_SCREENER=true -> mock; anything else -> live

interface ScreenerFilter {
  marketCapMin?: number;
  marketCapMax?: number;
  peRatioMax?: number;
  pegRatioMax?: number;
  fcfYieldMin?: number;
  revenueGrowthMin?: number;
  grossMarginMin?: number;
  dividendYieldMin?: number;
  sector?: string;
  country?: string;
}

// Interface wird vom Service importiert

const ScreenerPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [filters, setFilters] = useState<ScreenerFilter>({});
  const [selectedPreset, setSelectedPreset] = useState<string>('qualyz');
  const [sortBy, setSortBy] = useState<string>('symbol');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [saveMessage, setSaveMessage] = useState<string>('');
  const [addMessage, setAddMessage] = useState<string>('');
  const [searchParams] = useSearchParams();

  // Decide whether to use mock data based on env flag
  const useMock = (import.meta as any).env?.VITE_USE_MOCK_SCREENER === 'true';

  // Preset-Definitionen
  const presets = {
    qualyz: {
      name: 'Qualyz',
      description: 'Unsere Premium-Strategie mit 6 Kriterien',
      icon: <Target className="w-4 h-4" />,
      filters: {
        pegRatioMax: 2.0,
        fcfYieldMin: 5.0,
        marketCapMin: 100000000,
        revenueGrowthMin: 5.0,
        grossMarginMin: 40.0
      }
    },
    graham: {
      name: 'Graham/Value',
      description: 'Klassische Value-Investing-Strategie',
      icon: <BarChart3 className="w-4 h-4" />,
      filters: {
        peRatioMax: 15.0,
        pegRatioMax: 1.5
      }
    },
    peterLynch: {
      name: 'Peter Lynch',
      description: 'PEG ≈ 1.0 Strategie',
      icon: <TrendingUp className="w-4 h-4" />,
      filters: {
        pegRatioMax: 1.2,
        revenueGrowthMin: 10.0
      }
    },
    ruleOf40: {
      name: 'Rule of 40',
      description: 'Wachstum % + Marge % ≥ 40',
      icon: <Zap className="w-4 h-4" />,
      filters: {
        revenueGrowthMin: 20.0,
        grossMarginMin: 20.0
      }
    },
    strongGrowth: {
      name: 'Strong Growth',
      description: 'Fokus auf hohes Wachstum',
      icon: <TrendingUp className="w-4 h-4" />,
      filters: {
        revenueGrowthMin: 20.0
      }
    }
  };

  // Screener-Query (umschaltbar: Mock vs Live API)
  const { data: screenerResults, isLoading, error } = useQuery({
    queryKey: ['screener', useMock, filters, sortBy, sortOrder, currentPage, pageSize],
    queryFn: () => {
      const request = {
        filters,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: currentPage,
        page_size: pageSize
      };
      if (useMock) {
        return screenerService.getMockScreenerResponse(request as any);
      }
      return screenerService.screenStocks(request as any);
    },
    staleTime: 5 * 60 * 1000, // 5 Minuten
    retry: false,
  });

  // Preset anwenden
  const applyPreset = (presetKey: string) => {
    setSelectedPreset(presetKey);
    const preset = presets[presetKey as keyof typeof presets];
    if (preset) {
      setFilters(preset.filters);
      setCurrentPage(1);
    }
  };

  // Filter aktualisieren
  const updateFilter = (key: keyof ScreenerFilter, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  // Sortierung ändern
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Formatierung für Zahlen
  const formatNumber = (value: number | undefined, decimals: number = 2): string => {
    if (value === undefined || value === null) return '-';
    return value.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  // Formatierung für Währung
  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return '-';
    if (value >= 1e9) return `€${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `€${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `€${(value / 1e3).toFixed(2)}K`;
    return `€${value.toFixed(2)}`;
  };

  // Formatierung für Prozente
  const formatPercentage = (value: number | undefined): string => {
    if (value === undefined || value === null) return '-';
    return `${value.toFixed(2)}%`;
  };

  // Watchlist: serverseitig (DB), nur für eingeloggte Nutzer
  const handleAddToWatchlist = async (symbol: string) => {
    if (!isAuthenticated) {
      setAddMessage('Bitte einloggen, um zur Watchlist hinzuzufügen.');
      return;
    }
    try {
      await WatchlistService.add(symbol);
      setAddMessage('Zur Watchlist hinzugefügt.');
    } catch (e: any) {
      const msg = (e?.message || '').toString();
      setAddMessage(msg ? `Watchlist: ${msg}` : 'Watchlist-Update fehlgeschlagen.');
    } finally {
      setTimeout(() => setAddMessage(''), 3000);
    }
  };

  // Beim Einstieg: gespeicherten Filter anwenden, falls Query-Param vorhanden
  useEffect(() => {
    const savedId = searchParams.get('saved');
    if (savedId && isAuthenticated && user?.email) {
      const raw = localStorage.getItem(`saved_filters_${user.email}`);
      if (raw) {
        try {
          const list = JSON.parse(raw);
          const entry = list.find((e: any) => e.id === savedId);
          if (entry) {
            setFilters(entry.filters || {});
            setSortBy(entry.sortBy || 'symbol');
            setSortOrder(entry.sortOrder || 'asc');
            setSelectedPreset('custom');
          }
        } catch {
          /* ignore */
        }
      }
    }
  }, [searchParams, isAuthenticated, user?.email]);

  // Filter speichern (lokal, pro User)
  const handleSaveFilters = () => {
    if (!isAuthenticated || !user?.email) {
      setSaveMessage('Bitte einloggen, um Filter zu speichern.');
      return;
    }
    const name = window.prompt('Name für diesen Filter-Satz (z.B. \"Wachstum\" )?');
    if (!name) return;
    const key = `saved_filters_${user.email}`;
    const existing = localStorage.getItem(key);
    const list = existing ? JSON.parse(existing) : [];
    const entry = {
      id: crypto.randomUUID(),
      name,
      filters,
      sortBy,
      sortOrder,
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem(key, JSON.stringify([entry, ...list].slice(0, 20))); // max 20
    setSaveMessage('Filter gespeichert');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Aktien-Screener
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Entdecken Sie qualitativ hochwertige Investment-Möglichkeiten mit unseren bewährten Strategien
          </p>
        </div>

        {/* Preset-Auswahl */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Strategie auswählen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(presets).map(([key, preset]) => (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedPreset === key
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="text-blue-600 dark:text-blue-400">
                    {preset.icon}
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {preset.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {preset.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Filter-Interface */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Filter anpassen
            </h3>
          </div>
          <div className="flex items-center gap-3">
            {saveMessage && <span className="text-sm text-green-600 dark:text-green-400">{saveMessage}</span>}
            <button
              onClick={handleSaveFilters}
              className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Filter speichern
            </button>
          </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Market Cap */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Marktwert (Min.)
              </label>
              <select
                value={filters.marketCapMin || ''}
                onChange={(e) => updateFilter('marketCapMin', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Alle</option>
                <option value="100000000">€100M+</option>
                <option value="1000000000">€1B+</option>
                <option value="10000000000">€10B+</option>
                <option value="100000000000">€100B+</option>
              </select>
            </div>

            {/* P/E Ratio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                KGV (Max.)
              </label>
              <input
                type="number"
                placeholder="z.B. 15"
                value={filters.peRatioMax || ''}
                onChange={(e) => updateFilter('peRatioMax', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* PEG Ratio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                PEG (Max.)
              </label>
              <input
                type="number"
                placeholder="z.B. 2.0"
                step="0.1"
                value={filters.pegRatioMax || ''}
                onChange={(e) => updateFilter('pegRatioMax', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* FCF Yield */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                FCF-Yield (Min. %)
              </label>
              <input
                type="number"
                placeholder="z.B. 5.0"
                step="0.1"
                value={filters.fcfYieldMin || ''}
                onChange={(e) => updateFilter('fcfYieldMin', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Revenue Growth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Umsatzwachstum (Min. %)
              </label>
              <input
                type="number"
                placeholder="z.B. 10.0"
                step="0.1"
                value={filters.revenueGrowthMin || ''}
                onChange={(e) => updateFilter('revenueGrowthMin', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Gross Margin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Bruttomarge (Min. %)
              </label>
              <input
                type="number"
                placeholder="z.B. 40.0"
                step="0.1"
                value={filters.grossMarginMin || ''}
                onChange={(e) => updateFilter('grossMarginMin', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Dividend Yield */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Dividendenrendite (Min. %)
              </label>
              <input
                type="number"
                placeholder="z.B. 3.0"
                step="0.1"
                value={filters.dividendYieldMin || ''}
                onChange={(e) => updateFilter('dividendYieldMin', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Land
              </label>
              <select
                value={filters.country || ''}
                onChange={(e) => updateFilter('country', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Alle Länder</option>
                <option value="USA">USA</option>
                <option value="Germany">Deutschland</option>
                <option value="UK">Großbritannien</option>
                <option value="France">Frankreich</option>
                <option value="Switzerland">Schweiz</option>
                <option value="Netherlands">Niederlande</option>
                <option value="Japan">Japan</option>
                <option value="Canada">Kanada</option>
                <option value="Australia">Australien</option>
              </select>
            </div>

            {/* Sector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sektor
              </label>
              <select
                value={filters.sector || ''}
                onChange={(e) => updateFilter('sector', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Alle Sektoren</option>
                <option value="Technology">Technologie</option>
                <option value="Healthcare">Gesundheitswesen</option>
                <option value="Financial">Finanzen</option>
                <option value="Consumer">Konsumgüter</option>
                <option value="Industrial">Industrie</option>
                <option value="Energy">Energie</option>
                <option value="Materials">Materialien</option>
                <option value="Utilities">Versorger</option>
                <option value="Real Estate">Immobilien</option>
                <option value="Communication">Kommunikation</option>
              </select>
            </div>
          </div>
        </div>

        {/* Ergebnisse */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Suchergebnisse
              </h3>
              {screenerResults && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {screenerResults.total} Aktien gefunden
                </span>
              )}
            </div>
          </div>

          {addMessage && (
            <div className="px-6 py-2 text-sm text-green-700 dark:text-green-300">{addMessage}</div>
          )}

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Lade Ergebnisse...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <p className="text-red-600 dark:text-red-400">{`Fehler beim Laden der Ergebnisse${(error as any)?.message ? `: ${(error as any).message}` : ''}`}</p>
            </div>
          ) : screenerResults?.results?.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400">Keine Aktien gefunden, die den Kriterien entsprechen</p>
            </div>
          ) : (
            <>
              {/* Tabelle */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('symbol')}>
                        Symbol
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('name')}>
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('price')}>
                        Kurs
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('marketCap')}>
                        Marktwert
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('peRatio')}>
                        KGV
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('pegRatio')}>
                        PEG
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('fcfYield')}>
                        FCF-Yield
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('revenueGrowth')}>
                        Wachstum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('grossMargin')}>
                        Marge
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('qualyzScore')}>
                        Qualyz-Score
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {screenerResults?.results?.map((stock) => (
                      <tr key={stock.symbol} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {stock.symbol}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {stock.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {stock.sector} • {stock.country}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatCurrency(stock.price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatCurrency(stock.market_cap)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatNumber(stock.pe_ratio)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatNumber(stock.peg_ratio)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatPercentage(stock.fcf_yield)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatPercentage(stock.revenue_growth)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatPercentage(stock.gross_margin)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {stock.qualyz_score ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              stock.qualyz_score >= 80 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                              stock.qualyz_score >= 60 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {stock.qualyz_score}/100
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleAddToWatchlist(stock.symbol)}
                            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            Auf Watchlist
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginierung */}
              {screenerResults && screenerResults.total > pageSize && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Zeige {((currentPage - 1) * pageSize) + 1} bis {Math.min(currentPage * pageSize, screenerResults.total)} von {screenerResults.total} Ergebnissen
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Zurück
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={currentPage * pageSize >= screenerResults.total}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Weiter
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreenerPage;
